import { addDoc, collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '@/config/firebase';
import type {
  PayrollImportDocument,
  PayrollImportPayload,
  PayrollImportCollaboratorSummary,
  PayrollMatch,
  NormalizedPayrollEmployeeRecord,
} from './payrollTypes';

function isConfirmedMatch(match: PayrollMatch): match is PayrollMatch & { collaborator: NonNullable<PayrollMatch['collaborator']> } {
  return Boolean(match.collaborator) && !['conflict_id', 'conflict_cpf', 'not_found'].includes(match.status);
}

/**
 * Constrói o payload final para importação (apenas em memória).
 * Filtra conflitos e pendências sem colaborador.
 */
export function buildPayrollImportPayload(
  run: { competence?: string } | null,
  matches: PayrollMatch[],
): PayrollImportPayload {
  const confirmed = matches.filter(isConfirmedMatch);

  const records = confirmed.map((m) => ({
    collaboratorId: m.collaborator!.id,
    collaboratorName: m.collaborator!.name,
    payrollEmployeeId: m.payroll.employeeId,
    payrollRecord: m.payroll as NormalizedPayrollEmployeeRecord,
    matchedBy: m.matchedBy ?? 'employeeId',
  }));

  const totalsByPD = records.reduce<Record<'earning' | 'deduction', number>>((acc, record) => {
    record.payrollRecord.items.forEach((item) => {
      if (item.pdType === 'earning' || item.pdType === 'deduction') {
        acc[item.pdType] = (acc[item.pdType] ?? 0) + (item.amount ?? 0);
      }
    });
    return acc;
  }, { earning: 0, deduction: 0 });

  const totals = records.reduce<
    Record<
      'earning' | 'deduction',
      {
        total: number;
        byCode: Record<string, { total: number; description: string }>;
      }
    >
  >((acc, record) => {
    const normalizeCode = (code: string, description: string): string => {
      if (code.length <= 2) {
        const found = description.match(/\b(\d{3,4})\b/);
        if (found) return found[1];
      }
      return code;
    };

    const normalizeDescription = (desc: string): string =>
      desc.replace(/^[PD]\s+\d{2,4}\s+/, '').trim() || desc.trim();

    record.payrollRecord.items.forEach((item) => {
      if (item.pdType !== 'earning' && item.pdType !== 'deduction') return;
      const codeKey = normalizeCode(item.code, item.description);
      const descKey = normalizeDescription(item.description);
      const bucket = acc[item.pdType] ?? { total: 0, byCode: {} };
      bucket.total += item.amount ?? 0;
      const codeBucket = bucket.byCode[codeKey] ?? { total: 0, description: descKey };
      codeBucket.total += item.amount ?? 0;
      codeBucket.description = codeBucket.description || descKey;
      bucket.byCode[codeKey] = codeBucket;
      acc[item.pdType] = bucket;
    });
    return acc;
  }, { earning: { total: 0, byCode: {} }, deduction: { total: 0, byCode: {} } });

  return {
    run,
    competence: run?.competence,
    records,
    totals,
  };
}

/**
 * Persiste o employeeId no documento do colaborador escolhido.
 * (Front-only nesta fase; sem batch para simplificar.)
 */
export async function savePayrollEmployeeAssignments(matches: PayrollMatch[]): Promise<void> {
  const confirmed = matches.filter(isConfirmedMatch);

  if (!confirmed.length) return;

  const batch = writeBatch(db);

  confirmed.forEach((match) => {
    const ref = doc(db, 'collaborators_private', match.collaborator!.id);
    batch.update(ref, { payrollEmployeeId: match.payroll.employeeId });
  });

  await batch.commit();
}

function stripUndefined(value: any): any {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) return value.map(stripUndefined).filter((v) => v !== undefined);
  if (value instanceof Date) return value;
  if (typeof value === 'object') {
    return Object.entries(value).reduce<Record<string, any>>((acc, [key, val]) => {
      const cleaned = stripUndefined(val);
      if (cleaned !== undefined) {
        acc[key] = cleaned;
      }
      return acc;
    }, {});
  }
  return value;
}

/**
 * Salva um documento de importação de folha.
 * Somente registros confirmados são persistidos.
 */
export async function savePayrollImport(payload: PayrollImportPayload, user: { uid: string; email?: string | null; displayName?: string | null }): Promise<PayrollImportDocument> {
  const cleanedRun = stripUndefined(payload.run ?? null);
  const competence = payload.competence ?? null;
  const [year, month] = competence?.includes('-') ? competence.split('-') : [undefined, undefined];
  const collaboratorSummaries: PayrollImportCollaboratorSummary[] = payload.records.map((record) => {
    const totals = record.payrollRecord.items.reduce(
      (acc, item) => {
        if (item.pdType === 'earning') {
          acc.earningsTotal += item.amount ?? 0;
        }
        if (item.pdType === 'deduction') {
          acc.deductionsTotal += item.amount ?? 0;
        }
        return acc;
      },
      { earningsTotal: 0, deductionsTotal: 0 },
    );

    return {
      collaboratorId: record.collaboratorId,
      collaboratorName: record.collaboratorName,
      payrollEmployeeId: record.payrollEmployeeId,
      earningsTotal: totals.earningsTotal,
      deductionsTotal: totals.deductionsTotal,
      payrollSalary: record.payrollRecord.baseSalary ?? null,
    };
  });

  const docRef = await addDoc(collection(db, 'payroll_imports'), {
    competence,
    competenceYear: year ?? null,
    competenceMonth: month ?? null,
    run: cleanedRun ?? null,
    collaborators: stripUndefined(collaboratorSummaries) ?? [],
    totals: stripUndefined(payload.totals ?? {}) ?? {},
    createdAt: serverTimestamp(),
    createdByUid: user.uid,
    createdByEmail: user.email ?? null,
    createdByName: user.displayName ?? null,
    audit: stripUndefined(payload.audit ?? []) ?? [],
  });

  if (payload.records.length) {
    const batch = writeBatch(db);
    payload.records.forEach((record) => {
      const recordRef = doc(db, 'payroll_imports', docRef.id, 'records', record.collaboratorId);
      batch.set(recordRef, stripUndefined({ ...record, createdAt: serverTimestamp() }));
    });
    await batch.commit();
  }

  return {
    id: docRef.id,
    run: payload.run,
    competence: payload.competence,
    competenceYear: year,
    competenceMonth: month,
    collaborators: collaboratorSummaries,
    totals: payload.totals,
    createdAt: new Date(),
    createdByUid: user.uid,
    createdByEmail: user.email ?? null,
    createdByName: user.displayName ?? null,
    audit: payload.audit,
  };
}
